import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';

type PaletteToken = 'primary' | 'warning';

type IllustrationProps = {
  PrimaryIcon: SvgIconComponent;
  SecondaryIcon: SvgIconComponent;
  accentPalette: PaletteToken;
  lineWidths: [string, string, string];
};

export const Illustration = ({
  PrimaryIcon,
  SecondaryIcon,
  accentPalette,
  lineWidths,
}: IllustrationProps) => {
  const accentColor = `${accentPalette}.main`;

  return (
    <Box
      aria-hidden="true"
      sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: 256, md: 320 },
          height: { xs: 256, md: 320 },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'action.hover',
            borderRadius: '2rem',
            transform: 'rotate(3deg)',
            opacity: 0.5,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'action.selected',
            borderRadius: '2rem',
            transform: 'rotate(-3deg)',
            opacity: 0.8,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'background.paper',
            borderRadius: '2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            boxShadow: theme =>
              `0 12px 32px ${alpha(theme.palette[accentPalette].main, 0.08)}`,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 96,
              height: 96,
              bgcolor: 'action.hover',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <PrimaryIcon sx={{ fontSize: 60, color: accentColor }} />
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {lineWidths.map((width, index) => (
              <Box
                key={index}
                sx={{
                  height: 8,
                  width,
                  bgcolor: 'action.hover',
                  borderRadius: 4,
                  mx: 'auto',
                }}
              />
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            bottom: -16,
            right: -16,
            width: 96,
            height: 96,
            bgcolor: 'background.paper',
            backdropFilter: 'blur(12px)',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SecondaryIcon sx={{ fontSize: 40, color: accentColor }} />
        </Box>
      </Box>
    </Box>
  );
};
